import calculate_percentile from "./_calculatedPercentile";
import charutil from "./ChartUtils";
import { deriveProgramAndAgeGroup, getNorm } from '../utils/normHelpers';

function createAthleteHistoryChart(
  normsObject,
  showNorms,
  athleteEvals,
  testToPlot,
  testTitleString,
  onMarkerDoubleClick,
  lastClickRef,
  plot_percentile
) {
  let validChart = false;
  let series = [];
  let seriesData = [];
  let normSeriesDataMax = [];
  let normSeriesDataMin = [];

  // Sort athleteEvals by age at measurementDateTime
  athleteEvals.sort((a, b) => {
    const ageA =
      new Date(a.measurementDate) - new Date(a.athleteProfileForTest.dob);
    const ageB =
      new Date(b.measurementDate) - new Date(b.athleteProfileForTest.dob);
    return ageA - ageB;
  });

  let anyNormsAvailable = false;

  athleteEvals.forEach((evalData) => {
    const testData = evalData[testToPlot];
    if (!testData) return;

    const { programKey, ageGroup } = deriveProgramAndAgeGroup(
      evalData.athleteProfileForTest.program,
      evalData.athleteProfileForTest.group,
      evalData.athleteProfileForTest.age
    );

    const gender = evalData.athleteProfileForTest.gender;
    const norms = getNorm(testToPlot, programKey, ageGroup, gender, normsObject);
    const normsAvailable = norms.mean !== null;
    if (normsAvailable) anyNormsAvailable = true;

    if (
      testData.isValid &&
      typeof testData.value === "number" &&
      testData.value !== -99999.0
    ) {
      validChart = true;

      const measureDateTime = evalData.measurementDate;
      const age =
        (new Date(measureDateTime) -
          new Date(evalData.athleteProfileForTest.dob)) /
        (1000 * 60 * 60 * 24 * 365);
      const [measureDate, measureTimeWithMs] = measureDateTime.split("T");
      const measureTime = measureTimeWithMs.split(".")[0];

      const absoluteValue = testData.value;
      let percentileValue = normsAvailable ? calculate_percentile(absoluteValue, norms.mean, norms.sd) : null;

      if (
        testTitleString.includes("[s]") ||
        testTitleString.includes("[sec]")
      ) {
        percentileValue = 100.0 - percentileValue;
      }

      const valueToPlot = plot_percentile ? percentileValue : absoluteValue;

      const tooltipString = createTooltipString(
        absoluteValue,
        percentileValue,
        plot_percentile,
        normsAvailable,
        evalData.athleteProfileForTest.name,
        measureTime,
        measureDate,
        age
      );

      seriesData.push({
        x: new Date(measureDateTime).getTime(),
        y: valueToPlot,
        customTooltip: tooltipString,
      });

      // Norms data
      if (normsAvailable && !plot_percentile) {
        normSeriesDataMax.push({
          x: new Date(measureDateTime).getTime(),
          y: norms.mean + 3 * norms.sd,
          customTooltip:
            " - Norms max (3SD)",
        });
        normSeriesDataMin.push({
          x: new Date(measureDateTime).getTime(),
          y: norms.mean - 3 * norms.sd,
          customTooltip:
            " - Norms min (3SD)",
        });
      }
    }
  });

  if (!validChart) {
    return {
      series: [],
      options: {},
    };
  } else {
    series.push({
      name: "Athlete",
      data: seriesData,
      color: "#FDBA31",
    }); // Dark Gold: #FFD700

    if (!plot_percentile && anyNormsAvailable) {
      series.push({
        name: "Norms Max",
        data: normSeriesDataMax,
        color: "#000000",
        visible: showNorms,
      });
      series.push({
        name: "Norms Min",
        data: normSeriesDataMin,
        color: "#000000",
        visible: showNorms,
      });
    }
  }

  // Adjust marker sizes based on series count
  const seriesCount = series.length;
  let markerSize = 8;
  let strokeWidth = 1.5;
  let xFontSize = 16;
  if (seriesCount >= 3 && seriesCount <= 6) {
    xFontSize = 14;
    markerSize -= 1;
    strokeWidth = 1.25;
  } else if (seriesCount >= 7 && seriesCount <= 10) {
    xFontSize = 14;
    markerSize -= 2;
    strokeWidth = 1.0;
  } else if (seriesCount >= 11 && seriesCount <= 14) {
    xFontSize = 12;
    markerSize -= 3;
    strokeWidth = 0.75;
  } else if (seriesCount >= 15) {
    xFontSize = 9;
    markerSize -= 4;
    strokeWidth = 0.5;
  }

  const markers = generateMarkers(
    series,
    markerSize,
    strokeWidth,
    anyNormsAvailable,
    showNorms
  );

  // Calculate the min and max timestamps with margins
  const timestamps = series[0].data.map((d) => d.x);
  const minTimestamp = Math.min(...timestamps);
  const maxTimestamp = Math.max(...timestamps);
  const margin = (maxTimestamp - minTimestamp) * 0.1; // 10% margin on each side

  const startDate = new Date(minTimestamp);
  const endDate = new Date(maxTimestamp);
  const yearsDifference = endDate.getFullYear() - startDate.getFullYear();
  const monthsDifference =
    yearsDifference * 12 + (endDate.getMonth() - startDate.getMonth());

  // Calculate min and max values
  const allYValues = [
    ...seriesData.map((d) => d.y),
    ...normSeriesDataMax.map((d) => d.y),
    ...normSeriesDataMin.map((d) => d.y),
  ];
  const minValue = Math.min(...allYValues);
  const maxValue = Math.max(...allYValues);
  const estimateYAxisLimitsResult = charutil.estimateYAxisLimits(
    minValue,
    maxValue,
    false,
    false
  );

  const chartOptions = {
    series: series,
    options: {
      title: {
        text: testTitleString,
        align: "center",
        style: {
          fontSize: 32,
          fontWeight: "bold",
          color: "black",
        },
      },
      chart: {
        type: "line",
        height: 350,
        zoom: {
          enabled: false,
        },
        animations: {
          enabled: false,
          animateGradually: {
            enabled: false,
          },
          dynamicAnimation: {
            enabled: false,
          },
        },
        events: {
          markerClick: function (
            event,
            chartContext,
            { seriesIndex, dataPointIndex }
          ) {
            const marker =
              chartContext.w.config.series[seriesIndex].data[dataPointIndex];
            const customTooltip = marker.customTooltip;

            if (
              lastClickRef.current &&
              new Date().getTime() - lastClickRef.current < 300
            ) {
              lastClickRef.current = null;
              onMarkerDoubleClick(customTooltip);
            } else {
              lastClickRef.current = new Date().getTime();
            }
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      markers: markers,
      stroke: {
        show: series.map((s, idx) => (idx === 0 ? true : showNorms)),
        width: [4, showNorms ? 3 : 0, showNorms ? 3 : 0],
      },
      legend: {
        show: false,
      },
      yaxis: {
        min: plot_percentile
          ? 0.0
          : testTitleString.includes("PHV")
          ? Math.min(-5.0, estimateYAxisLimitsResult.min)
          : 0.0,
        max: plot_percentile
          ? 100
          : testTitleString.includes("PHV")
          ? Math.max(5.0, estimateYAxisLimitsResult.max)
          : estimateYAxisLimitsResult.max,
        labels: {
          style: {
            fontSize: 16,
            color: "white",
          },
          formatter: function (value) {
            return value === Math.round(value)
              ? value.toFixed(0)
              : value.toFixed(3);
          },
        },
        title: {
          text: plot_percentile ? "Percentile [%]" : "Absolute value",
          offsetX: -10,
          style: {
            fontSize: 20,
            fontWeight: "bold",
            color: "black",
          },
        },
      },
      xaxis: {
        type: "datetime",
        min: minTimestamp - margin,
        max: maxTimestamp + margin,
        tickAmount: Math.min(monthsDifference, 12),
        labels: {
          show: true,
          formatter: function (value) {
            const date = new Date(value);
            return date.toLocaleDateString("en-GB", {
              month: "short",
              year: "2-digit",
            });
          },
        },
        title: {
          text: "Evaluation Date",
          style: {
            fontSize: xFontSize,
            fontWeight: "bold",
            color: "black",
          },
        },
      },
      tooltip: {
        intersect: false,
        shared: false,
        theme: "dark",
        x: {
          formatter: function (value) {
            const date = new Date(value);
            return date.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "2-digit",
            });
          },
        },
        custom: function (opts) {
          const dataPoint =
            opts.ctx.w.config.series[opts.seriesIndex].data[
              opts.dataPointIndex
            ];
          const value = opts.series[opts.seriesIndex][opts.dataPointIndex];
          return value.toFixed(2) + dataPoint.customTooltip;
        },
        style: {
          colors: ["black"],
        },
      },
    },
  };

  return chartOptions;
}

// Helper Functions

function createTooltipString(
  absoluteValue,
  percentileValue,
  plot_percentile,
  normsAvailable,
  name,
  measureTime,
  measureDate,
  age
) {
  const valueInfo = plot_percentile
    ? normsAvailable
      ? `% (${absoluteValue.toFixed(2)})`
      : ""
    : normsAvailable && percentileValue != null
    ? ` (${percentileValue.toFixed(1)}%)`
    : "";
  return (
    valueInfo +
    ` for\n ${name} at\n ${measureTime} on\n ${measureDate} [${age.toFixed(
      1
    )} years old]`
  );
}

function generateMarkers(
  series,
  markerSize,
  strokeWidth,
  anyNormsAvailable,
  showNorms
) {
  const markers = {
    shape: [],
    size: [],
    strokeColors: [],
    strokeWidth: [],
    fillOpacity: [],
    hover: {
      size: markerSize,
    },
  };

  series.forEach((s, index) => {
    if (index === 0) {
      // Athlete series
      markers.shape.push("circle");
      markers.size.push(markerSize);
      markers.strokeColors.push("#FFFFFF");
      markers.strokeWidth.push(strokeWidth);
      markers.fillOpacity.push(1.0);
    } else if (anyNormsAvailable && showNorms) {
      markers.shape.push("circle");
      markers.size.push(0.5 * markerSize);
      markers.strokeColors.push("#FFFFFF");
      markers.strokeWidth.push(strokeWidth);
      markers.fillOpacity.push(1.0);
    } else {
      markers.shape.push("circle");
      markers.size.push(0);
      markers.strokeColors.push("#FFFFFF");
      markers.strokeWidth.push(0);
      markers.fillOpacity.push(0.0);
    }
  });

  return markers;
}

export default createAthleteHistoryChart;
